// The Dal Segno Machine: standard notation misses Turing completeness by ONE
// instruction — a repeat count that can reference another repeat. This exhibit
// adds it. Rehearsal letters become registers, ×(A−1) marks read/write them,
// and a conditional D.S. ("se A = 0") is branch-if-zero: exactly a Minsky
// counter machine, so these little scores compute. The performer carries a
// fuel gauge because, in this extended notation, "does the piece end?" is the
// halting problem. Pure semantics + preset pieces live in music/segnoMachine.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getChordPlayer } from '@/audio/player';
import { APP_ROUTES } from '@/navigation';
import { ScoreView } from '@/components/ScoreView';
import { segnoProgramToSong } from '@/music/segnoScore';
import {
  SEGNO_PRESETS,
  countLabel,
  jumpLabel,
  runSegnoMachine,
  type SegnoLine,
  type TraceEvent,
} from '@/music/segnoMachine';

/** The performer's tank: analysis of these scores can promise nothing more. */
const FUEL = 4000;
/** How much of the performance the ribbon shows. */
const RIBBON_WINDOW = 72;

const SEGNO = '\ue047'; // SMuFL segno (Bravura glyph)
const CODA = '\ue048'; // SMuFL coda
const REPEAT_L = '\ue040'; // SMuFL repeat-left barline
const REPEAT_R = '\ue041'; // SMuFL repeat-right barline

const LETTER_TINT: Record<string, string> = {
  A: 'var(--accent)',
  B: '#d98e04',
  C: '#3b82f6',
};

const TEMPI = [
  { id: 'adagio', label: 'Adagio', ms: 300 },
  { id: 'allegro', label: 'Allegro', ms: 110 },
  { id: 'presto', label: 'Presto', ms: 25 },
] as const;

function Glyph({ text }: { text: string }) {
  return <span className="segno-glyph">{text}</span>;
}

function lineGlyphs(line: SegnoLine, lines: SegnoLine[]) {
  if (line.kind === 'mark') {
    return (
      <>
        <Glyph text={line.fine ? CODA : SEGNO} />
        <span className="segno-directive">
          {line.fine ? 'Fine' : (line.jumps ?? []).map((j) => jumpLabel(j, lines)).join(' · ')}
        </span>
      </>
    );
  }
  return (
    <>
      <Glyph text={REPEAT_L} />
      <span className="segno-letter" style={{ borderColor: LETTER_TINT[line.letter] }}>
        {line.letter}
      </span>
      <Glyph text={REPEAT_R} />
      <span className="segno-count">{countLabel(line.expr)}</span>
      {line.jumps?.length ? (
        <span className="segno-directive">
          {line.jumps.map((j) => jumpLabel(j, lines)).join(' · ')}
        </span>
      ) : null}
    </>
  );
}

export function DalSegnoMachineExhibit() {
  const [presetId, setPresetId] = useState(SEGNO_PRESETS[0].id);
  const preset = SEGNO_PRESETS.find((p) => p.id === presetId) ?? SEGNO_PRESETS[0];
  const [inputs, setInputs] = useState<number[]>((preset.inputs ?? []).map((spec) => spec.default));
  const [tempo, setTempo] = useState<(typeof TEMPI)[number]['id']>('allegro');
  // The playhead: -1 = not yet begun; trace.length-1 = the performance is over.
  const [idx, setIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const soundedRef = useRef(-1);

  const result = useMemo(
    () => runSegnoMachine(preset.program, { inputs, fuel: FUEL }),
    [preset, inputs],
  );
  // The engraved sheet never changes with the sliders: the score is the
  // program, the soloists' inputs are the performance.
  const song = useMemo(() => segnoProgramToSong(preset.program, preset.title), [preset]);
  const trace = result.trace;
  const at: TraceEvent | undefined = idx >= 0 ? trace[idx] : undefined;
  const finished = idx >= trace.length - 1;
  const registers =
    at?.registers ?? Object.fromEntries(Object.keys(result.registers).map((k) => [k, 0]));

  const pickPreset = (id: string) => {
    const next = SEGNO_PRESETS.find((p) => p.id === id) ?? SEGNO_PRESETS[0];
    setPresetId(id);
    setInputs((next.inputs ?? []).map((spec) => spec.default));
    setIdx(-1);
    setPlaying(false);
  };

  useEffect(() => {
    if (!playing) return;
    const ms = TEMPI.find((t) => t.id === tempo)?.ms ?? 110;
    const timer = window.setInterval(() => {
      setIdx((i) => {
        if (i >= trace.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, ms);
    return () => window.clearInterval(timer);
  }, [playing, tempo, trace.length]);

  // Sound rides the playhead: one pluck per sounding bar (Presto stays silent —
  // 40 plucks a second is noise, and at that speed you're watching the math).
  useEffect(() => {
    if (!playing || tempo === 'presto') return;
    if (idx < 0 || idx === soundedRef.current) return;
    soundedRef.current = idx;
    const ev = trace[idx];
    if (ev?.kind !== 'bar') return;
    const line = preset.program.lines[ev.line];
    if (line?.kind === 'play') void getChordPlayer().playMidi([...line.midi], 0.012);
  }, [idx, playing, tempo, trace, preset]);

  const step = () => setIdx((i) => Math.min(i + 1, trace.length - 1));
  const reset = () => {
    setIdx(-1);
    setPlaying(false);
    soundedRef.current = -1;
  };
  const fastForward = () => {
    setIdx(trace.length - 1);
    setPlaying(false);
  };

  const barsSoFar = idx < 0 ? 0 : trace.slice(0, idx + 1).filter((e) => e.kind === 'bar').length;
  const ribbonStart = Math.max(0, idx + 1 - RIBBON_WINDOW);
  const ribbon = idx < 0 ? [] : trace.slice(ribbonStart, idx + 1);

  return (
    <div className="lab-exhibit" data-testid="lab-segno">
      <p className="lab-lede">
        Ordinary repeats compare a pass counter with written numbers, so every chart ends. If a
        repeat count can refer to another section&apos;s count, written ×(A−1), rehearsal letters
        act as registers and a conditional D.S. as a branch-if-zero. That makes notation a Minsky
        counter machine, which is Turing complete. The five scores below compute. Whether a piece
        ends is then the halting problem, so the performer stops after a fixed number of bars (the
        fuel).{' '}
        <Link to={APP_ROUTES.articleSegno} data-testid="segno-article-link">
          Read the essay
        </Link>
      </p>

      <div className="lab-chip-row" role="group" aria-label="Piece">
        {SEGNO_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`view-toggle-btn segno-preset${p.id === presetId ? ' active' : ''}`}
            aria-pressed={p.id === presetId}
            data-testid={`segno-preset-${p.id}`}
            onClick={() => pickPreset(p.id)}
          >
            {p.title}
          </button>
        ))}
      </div>

      <p className="segno-description">{preset.description}</p>

      <div className="lab-controls">
        {(preset.inputs ?? []).map((spec, slot) => (
          <label className="segno-input" key={slot}>
            {spec.label}:{' '}
            <strong data-testid={`segno-input-${slot}`}>{inputs[slot] ?? spec.default}</strong>
            <input
              type="range"
              min={spec.min}
              max={spec.max}
              value={inputs[slot] ?? spec.default}
              aria-label={spec.label}
              onChange={(e) => {
                const v = Number(e.target.value);
                setInputs((prev) => prev.map((x, i) => (i === slot ? v : x)));
                setIdx(-1);
                setPlaying(false);
              }}
            />
          </label>
        ))}
        <div className="segno-transport">
          <button
            type="button"
            className="btn btn-sm"
            data-testid="segno-play"
            onClick={() => {
              if (finished) reset();
              setPlaying((p) => !p);
            }}
          >
            {playing ? 'Pause' : 'Perform'}
          </button>
          <button type="button" className="btn btn-sm" data-testid="segno-step" onClick={step}>
            Step
          </button>
          <button
            type="button"
            className="btn btn-sm"
            data-testid="segno-ffwd"
            onClick={fastForward}
          >
            To Fine
          </button>
          <button type="button" className="btn btn-sm" data-testid="segno-reset" onClick={reset}>
            Reset
          </button>
          <select
            value={tempo}
            aria-label="Tempo"
            onChange={(e) => setTempo(e.target.value as (typeof TEMPI)[number]['id'])}
          >
            {TEMPI.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* The sheet itself — real engraving of the program. The playhead rides
          the trace: the current instruction is the current written bar. */}
      <div className="segno-sheet" data-testid="segno-sheet">
        <ScoreView
          song={song}
          options={{ spaceSize: 7, systemWidth: 96, maxBarsPerSystem: 4 }}
          playheadBeat={at ? at.line * 4 + 0.5 : null}
          ariaLabel={`${preset.title} engraved as sheet music`}
        />
      </div>

      <div className="lab-row lab-clock-row">
        <div className="segno-score" data-testid="segno-score">
          {preset.program.lines.map((line, i) => (
            <div
              key={i}
              className={`segno-line${at?.line === i ? ' active' : ''}`}
              data-testid={`segno-line-${i}`}
            >
              <span className="segno-lineno">{i + 1}</span>
              {lineGlyphs(line, preset.program.lines)}
              {at?.line === i && at.kind === 'bar' && (
                <span className="segno-passcount">
                  bar {at.pass} of {at.count}
                </span>
              )}
              {at?.line === i && at.kind === 'tacet' && (
                <span className="segno-passcount">tacet</span>
              )}
              {line.comment && <span className="segno-comment">{line.comment}</span>}
            </div>
          ))}
        </div>

        <div className="lab-panel">
          <div className="lab-panel-title">Registers</div>
          <div className="segno-registers" data-testid="segno-registers">
            {Object.keys(registers)
              .sort()
              .map((letter) => (
                <span
                  key={letter}
                  className="segno-reg"
                  style={{ borderColor: LETTER_TINT[letter] }}
                >
                  {letter} = <strong>{registers[letter]}</strong>
                </span>
              ))}
          </div>

          <div className="lab-panel-title" style={{ marginTop: 14 }}>
            Fuel
          </div>
          <div
            className={`segno-fuel${!result.halted ? ' exhausted' : ''}`}
            data-testid="segno-fuel"
          >
            <div
              className="segno-fuel-bar"
              style={{ width: `${Math.min(100, (barsSoFar / FUEL) * 100)}%` }}
            />
            <span className="segno-fuel-label">
              {barsSoFar} / {FUEL} bars
            </span>
          </div>

          {finished && (
            <p className="segno-verdict" data-testid="segno-verdict">
              {result.halted ? 'Fine reached. ' : 'Fuel exhausted. '}
              {preset.verdict(result, inputs)}
            </p>
          )}
        </div>
      </div>

      <div className="segno-ribbon" data-testid="segno-ribbon" aria-label="Performance so far">
        {ribbon.map((e, i) => {
          const key = ribbonStart + i;
          if (e.kind === 'mark') return <span key={key} className="segno-cell mark" />;
          if (e.kind === 'fine')
            return (
              <span key={key} className="segno-cell fine">
                <Glyph text={CODA} />
              </span>
            );
          const tint = LETTER_TINT[e.letter ?? ''] ?? 'var(--muted)';
          return e.kind === 'tacet' ? (
            <span key={key} className="segno-cell tacet" style={{ borderColor: tint }} />
          ) : (
            <span key={key} className="segno-cell bar" style={{ background: tint }}>
              {e.letter}
            </span>
          );
        })}
        {idx < 0 && (
          <span className="segno-ribbon-hint">Press Perform to play the score bar by bar.</span>
        )}
      </div>
    </div>
  );
}
